import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Home, ChevronDown, ChevronRight, LogOut } from 'lucide-react';
import { NAV_CATEGORIES, NavCategoryItem } from './NavCategories';
import type { Household, UserProfile } from '@/types';

const ALWAYS_VISIBLE_IDS = new Set(['settings', 'explore', 'influencer', 'feedback', 'changelog']);

interface SidebarProps {
  visibleItemIds: Set<string>;
  conditionalFlags: {
    hasCoParenting: boolean;
    hasElderCare: boolean;
    hasMunicipal: boolean;
  };
  currentHousehold?: Household;
  user: UserProfile;
  onLogout: () => void;
}

function isItemVisible(
  item: NavCategoryItem,
  visibleItemIds: Set<string>,
  conditionalFlags: SidebarProps['conditionalFlags']
): boolean {
  if (item.conditional === 'coParenting') return conditionalFlags.hasCoParenting;
  if (item.conditional === 'elderCare') return conditionalFlags.hasElderCare;
  if (item.conditional === 'municipal') return conditionalFlags.hasMunicipal;
  return visibleItemIds.has(item.id) || ALWAYS_VISIBLE_IDS.has(item.id);
}

export const Sidebar: React.FC<SidebarProps> = ({
  visibleItemIds,
  conditionalFlags,
  currentHousehold,
  user,
  onLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const STORAGE_KEY = 'sidebar-collapsed';

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const toggleCategory = (id: string) => {
    setCollapsed(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));

  return (
    <div className="w-56 h-screen flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex-shrink-0">
      {/* Brand */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-slate-100 dark:border-slate-800">
        <div className="bg-brand text-white rounded-xl p-1.5 flex-shrink-0">
          <Home className="w-4 h-4" />
        </div>
        <span className="text-sm font-black text-slate-900 dark:text-white tracking-tight">
          EverydayOS
        </span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto no-scrollbar py-3 px-2 space-y-0.5">
        {NAV_CATEGORIES.map((category) => {
          // Filter out 'connected' category if none of its conditionals are true
          if (category.id === 'connected') {
            const { hasCoParenting, hasElderCare, hasMunicipal } = conditionalFlags;
            if (!hasCoParenting && !hasElderCare && !hasMunicipal) return null;
          }

          const visibleItems = category.items.filter(item =>
            isItemVisible(item, visibleItemIds, conditionalFlags)
          );

          // Direct-path categories (e.g. Dashboard) render as a single link
          if (category.directPath) {
            const active = isActive(category.directPath);
            return (
              <button
                key={category.id}
                onClick={() => navigate(category.directPath!)}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-bold transition-all',
                  active
                    ? 'bg-brand text-white shadow-sm shadow-brand/20'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-brand'
                )}
              >
                {category.icon && <category.icon className="w-4 h-4 flex-shrink-0" />}
                {category.label}
              </button>
            );
          }

          if (visibleItems.length === 0) return null;

          const isExpanded = !collapsed[category.id];

          return (
            <div key={category.id}>
              {/* Category header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors mt-2"
              >
                <span className="flex items-center gap-2">
                  {category.icon && <category.icon className="w-3.5 h-3.5" />}
                  {category.label}
                </span>
                {isExpanded
                  ? <ChevronDown className="w-3 h-3" />
                  : <ChevronRight className="w-3 h-3" />
                }
              </button>

              {/* Items */}
              {isExpanded && (
                <div className="space-y-0.5 mt-0.5">
                  {visibleItems.map(item => {
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.id}
                        onClick={() => navigate(item.path)}
                        className={clsx(
                          'w-full text-left px-4 py-2 rounded-xl text-sm font-bold transition-all',
                          active
                            ? 'bg-brand text-white shadow-sm shadow-brand/20'
                            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-brand'
                        )}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-100 dark:border-slate-800 px-3 py-3 space-y-1">
        {currentHousehold && (
          <div className="px-3 py-2">
            <p className="text-2xs font-black uppercase tracking-widest text-slate-400 truncate">
              {currentHousehold.name}
            </p>
          </div>
        )}
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
};
